import { CheckCircle } from "lucide-react";
import React, { useState } from "react";
import AdminPageLayout from "../../components/AdminPageLayout";
import ConfirmationModal from "../../components/ConfirmationModal";
import ContractModal from "../../components/ContractModal";
import ContractUploadModal from "../../components/ContractUploadModal";
import LivePriceModal from "../../components/LivePriceModal";
import TradableTimesModal from "../../components/TradableTimesModal";
import { useTheme } from "../../contexts/ThemeContext";
import { useToast } from "../../contexts/ToastContext";
import { useContractPrices } from "../../hooks/useContractPrices";
import { useContracts } from "../../hooks/useContracts";
import { useInlineEditing } from "../../hooks/useInlineEditing";
import contractsV2Service from "../../services/contractsV2Service";
import { Contract, CONTRACT_COLUMNS, ContractUpsertInput } from "../../types/contract";
import { formatFileSize, formatNumber } from "../../utils/numberUtils";
import ContractReferenceDataModal from "./Contracts/components/ContractReferenceDataModal";
import ContractsActionToolbar from "./Contracts/components/ContractsActionToolbar";
import ContractsTable from "./Contracts/components/ContractsTable";
import ContractUsageModal from "./Contracts/components/ContractUsageModal";

/** Download durations run from a couple of seconds to a few minutes; show whichever reads better. */
const formatDownloadDuration = (durationMs: number): string => {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
};

const ContractsPage: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const toast = useToast();

  // Logic hooks
  const {
    contracts,
    loading,
    error,
    pagination,
    sorting,
    search,
    filters,
    selection,
    actions: contractActions
  } = useContracts();

  const {
    livePrices,
    fetchingPrices,
    fetchingAllPrices,
    fetchPriceForContract,
    fetchAllPrices,
  } = useContractPrices();

  // Passing fetchContracts to reload data after save
  const {
    editingCell,
    pendingChanges,
    savingChanges,
    handleCellDoubleClick,
    handleCellChange,
    handleCellBlur,
    handleCellKeyDown,
    saveChanges,
    discardChanges
  } = useInlineEditing(contractActions.fetchContracts);

  // Modal states (kept local as they are UI specific)
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [contractModalMode, setContractModalMode] = useState<"create" | "edit">("create");
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [showChangesModal, setShowChangesModal] = useState(false);
  const [tradableTimesModalOpen, setTradableTimesModalOpen] = useState(false);
  const [tradableTimesContract, setTradableTimesContract] = useState<Contract | null>(null);
  const [livePriceModalOpen, setLivePriceModalOpen] = useState(false);
  const [livePriceContract, setLivePriceContract] = useState<Contract | null>(null);
  const [toleranceModalOpen, setToleranceModalOpen] = useState(false);
  const [tolerancePercent, setTolerancePercent] = useState(2);
  const [verifyMatchesModalOpen, setVerifyMatchesModalOpen] = useState(false);
  const [matchingConIdsForVerify, setMatchingConIdsForVerify] = useState<number[]>([]);
  const [usageContract, setUsageContract] = useState<Contract | null>(null);
  const [referenceDataContract, setReferenceDataContract] = useState<Contract | null>(null);


  const openCreateModal = () => {
    setSelectedContract(null);
    setContractModalMode("create");
    setContractModalOpen(true);
  };

  const openEditModal = (contract: Contract) => {
    setSelectedContract(contract);
    setContractModalMode("edit");
    setContractModalOpen(true);
  };

  const handleSaveContract = async (input: ContractUpsertInput) => {
    await contractsV2Service.upsertContract(input);
    await contractActions.fetchContracts();
  };

  const openTradableTimes = (contract: Contract) => {
    setTradableTimesContract(contract);
    setTradableTimesModalOpen(true);
  };

  const openLivePrice = (contract: Contract) => {
    setLivePriceContract(contract);
    setLivePriceModalOpen(true);
  };

  // Derived state
  const hasPendingChanges = pendingChanges.size > 0;

  if (loading && contracts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-12 w-12 border-b-2 ${
              isDarkMode ? "border-rp-amber" : "border-blue-600"
            } mx-auto`}
          />
          <p className={`mt-4 ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            Loading contracts...
          </p>
        </div>
      </div>
    );
  }

  const openToleranceModal = () => {
    setToleranceModalOpen(true);
  };

  const handleFindMatches = () => {
    const matchingConIds: number[] = [];
    
    // Find matches using percentage-based tolerance
    contracts.forEach(contract => {
      const priceData = livePrices[String(contract.conId)];
      if (!priceData) return;

      const ibkr = priceData.find(p => p.source === "IBKR")?.price;
      const yahoo = priceData.find(p => p.source === "YAHOO")?.price;
      const eodhd = priceData.find(p => p.source === "EODHD")?.price;

      if (ibkr && yahoo && eodhd && ibkr > 0 && yahoo > 0 && eodhd > 0) {
        // Calculate max allowed delta based on average price and tolerance percentage
        const avgPrice = (ibkr + yahoo + eodhd) / 3;
        const maxDelta = avgPrice * (tolerancePercent / 100);
        
        if (Math.abs(ibkr - yahoo) <= maxDelta && Math.abs(yahoo - eodhd) <= maxDelta) {
          matchingConIds.push(contract.conId);
        }
      }
    });

    setToleranceModalOpen(false);

    if (matchingConIds.length > 0) {
        selection.setSelectedConIds(new Set(matchingConIds));
        setMatchingConIdsForVerify(matchingConIds);
        setVerifyMatchesModalOpen(true);
    } else {
        toast.info(`No price matches found within ${tolerancePercent}% tolerance.`);
    }
  };

  const handleConfirmVerifyMatches = () => {
    contractActions.verifyContracts(matchingConIdsForVerify);
    setVerifyMatchesModalOpen(false);
    setMatchingConIdsForVerify([]);
  };

  return (
    <AdminPageLayout
      title="Contracts"
      description="Manage trading contracts"
      hideContentScroll={true}
      actions={
        /* Actions are now mainly in the toolbar, but layout supports header actions too. 
           We moved them to toolbar as per extracted component design, so this prop can be empty or used for page-level only.
           Actually, the design had ResponsiveActionButtons in header actions. 
           Let's keep them there? No, the plan extraction put them in ContractsActionToolbar.
           The Layout actions prop usually sits in the top right of the header. 
           The Toolbar sits inside the content area.
           In the previous file, they were passed to 'actions' prop of AdminPageLayout.
           But we ALSO extracted them to ContractsActionToolbar?
           Let's verify: The previous code had `actions={<> <ResponsiveActionButton ... /> </>}` in `AdminPageLayout`.
           The extracted `ContractsActionToolbar` HAS these buttons.
           So we should probably NOT pass them to `actions` anymore, OR we should split them.
           Toolbar has "Refresh", "Upload", "Download", "Fetch All", "Add".
           This seems redundant to have in Layout actions AND Toolbar.
           Usually Layout actions are for "Add New" etc.
           I will put them in the Toolbar component which is rendered in the body.
           So `actions` prop can be empty or null.
        */
        undefined
      }
    >
      <div className="h-full flex flex-col">
        <ContractsActionToolbar
          searchQuery={search.searchQuery}
          onSearchChange={search.setSearchQuery}
          searchColumn={search.searchColumn}
          onSearchColumnChange={search.setSearchColumn}
          optionsAllowed={filters.optionsAllowed}
          onOptionsAllowedChange={filters.setOptionsAllowed}
          verifiedSinceMs={filters.verifiedSinceMs}
          onVerifiedSinceMsChange={filters.setVerifiedSinceMs}
          selectionSize={selection.selectedConIds.size}
          loading={loading}
          downloading={contractActions.downloading}
          bulkActionLoading={contractActions.bulkActionLoading}
          fetchingAllPrices={fetchingAllPrices}
          hasContracts={contracts.length > 0}
          downloadProgress={contractActions.downloadProgress}
          onRefresh={contractActions.fetchContracts}
          onUpload={() => setUploadModalOpen(true)}
          onDownload={contractActions.downloadContracts}
          onCancelDownload={contractActions.cancelDownload}
          onFetchAllPrices={() => fetchAllPrices(contracts.filter(c => c.exchange !== "VALUE").map(c => c.conId))}
          onAdd={openCreateModal}
          onVerifySelected={() => contractActions.verifyContracts()}
          onUnverifySelected={() => contractActions.unverifyContracts()}
          onDeleteSelected={() => setDeleteConfirmOpen(true)}
          onVerifyMatches={openToleranceModal}
        />

        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {contractActions.downloadSummary && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-500">
            <CheckCircle className="h-4 w-4 flex-shrink-0" />
            <span>
              Saved{" "}
              <span className="font-mono">{contractActions.downloadSummary.fileName}</span> --{" "}
              {formatNumber(contractActions.downloadSummary.rowCount, 0)} contracts,{" "}
              {formatFileSize(contractActions.downloadSummary.byteCount)} in{" "}
              {formatDownloadDuration(contractActions.downloadSummary.durationMs)}.
            </span>
          </div>
        )}

        <div className="flex justify-between items-center mb-2">
            <div>
                 {/* Empty left side */}
            </div>
             {hasPendingChanges && (
                <div className="flex gap-2">
                    <button
                        onClick={discardChanges}
                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                        Discard Changes
                    </button>
                    <button
                        onClick={() => setShowChangesModal(true)}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 shadow-sm"
                    >
                        Review {pendingChanges.size} Changes
                    </button>
                </div>
            )}
        </div>

        <ContractsTable
          contracts={contracts}
          columns={CONTRACT_COLUMNS}
          loading={loading}
          selectedConIds={selection.selectedConIds}
          sortColumn={sorting.sortColumn}
          sortDirection={sorting.sortDirection}
          onSort={sorting.handleSort}
          onSelectAll={selection.handleSelectAll}
          onSelectOne={selection.handleSelectOne}
          editingCell={editingCell}
          editValue={editingCell?.value || ""}
          pendingChanges={pendingChanges}
          onCellDoubleClick={handleCellDoubleClick}
          onCellChange={handleCellChange}
          onCellBlur={() => handleCellBlur(contracts)}
          onCellKeyDown={(e) => handleCellKeyDown(e, contracts)}
          livePrices={livePrices}
          fetchingPrices={fetchingPrices}
          onFetchPrice={fetchPriceForContract}
          onOpenTradableTimes={openTradableTimes}
          onOpenLivePrice={openLivePrice}
          onOpenUsage={(contract) => setUsageContract(contract)}
          onOpenReferenceData={(contract) => setReferenceDataContract(contract)}
        />

        {/* Footer Pagination - reusing primitive, could be componentized too but simple enough here */}
        <div className={`mt-4 flex items-center justify-between ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            <div className="flex items-center gap-4 text-sm">
             <div className="flex items-center gap-2">
                <span>Show</span>
                <select
                  value={pagination.pageSize}
                  onChange={(e) => pagination.setPageSize(Number(e.target.value))}
                  className="border rounded p-1 dark:bg-rp-surface dark:border-rp-separator"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={500}>500</option>
                  <option value={1000}>1000</option>
                </select>
              </div>
              <span>
                Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to {Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems}
              </span>
            </div>
           <div className="flex gap-2">
             <button
               onClick={() => pagination.setCurrentPage(1)}
               disabled={pagination.currentPage === 1}
               className="px-2 py-1 text-sm rounded hover:bg-gray-100 disabled:opacity-50"
             >
               First
             </button>
             <button
               onClick={() => pagination.setCurrentPage(prev => Math.max(1, prev - 1))}
               disabled={pagination.currentPage === 1}
               className="px-2 py-1 text-sm rounded hover:bg-gray-100 disabled:opacity-50"
             >
               Prev
             </button>
             <span className="px-2 py-1 text-sm">
               Page {pagination.currentPage} of {pagination.totalPages}
             </span>
             <button
               onClick={() => pagination.setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
               disabled={pagination.currentPage === pagination.totalPages}
               className="px-2 py-1 text-sm rounded hover:bg-gray-100 disabled:opacity-50"
             >
               Next
             </button>
           </div>
        </div>
      </div>

      {/* Modals */}
      <ContractModal
        isOpen={contractModalOpen}
        onClose={() => setContractModalOpen(false)}
        onSave={handleSaveContract}
        mode={contractModalMode}
        contract={selectedContract}
      />

      <ContractUploadModal
        isOpen={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        onSuccess={() => {
          contractActions.fetchContracts();
        }}
      />

      <ConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={contractActions.deleteContracts}
        title="Delete Contracts"
        message={`Are you sure you want to delete ${selection.selectedConIds.size} selected contracts? This action cannot be undone.`}
        confirmText={contractActions.deleting ? "Deleting..." : "Delete"}
        loading={contractActions.deleting}
        isDestructive={true}
      />

      {/* Tolerance Input Modal */}
      {toleranceModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? "bg-rp-surface" : "bg-white"} rounded-lg shadow-xl max-w-sm w-full overflow-hidden`}>
            <div className={`flex items-center justify-between p-6 border-b ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
              <h2 className={`text-lg font-semibold ${isDarkMode ? "text-rp-grey" : "text-gray-900"}`}>
                Verify Price Matches
              </h2>
              <button
                onClick={() => setToleranceModalOpen(false)}
                className={`transition-colors ${isDarkMode ? "text-rp-grey/50 hover:text-rp-grey/70" : "text-gray-400 hover:text-gray-600"}`}
              >
                <span className="sr-only">Close</span>
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <p className={`text-sm mb-4 ${isDarkMode ? "text-rp-grey/70" : "text-gray-600"}`}>
                Find contracts where IBKR, Yahoo, and EODHD prices match within the specified tolerance.
              </p>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}>
                Price Tolerance (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={tolerancePercent}
                onChange={(e) => setTolerancePercent(Math.max(0, Math.min(100, Number(e.target.value))))}
                className={`w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 ${
                  isDarkMode
                    ? "bg-rp-surface border-rp-separator text-rp-grey focus:ring-rp-amber focus:border-rp-amber"
                    : "bg-white border-gray-300 text-gray-900 focus:ring-blue-500"
                }`}
              />
            </div>
            <div className={`flex items-center justify-end p-6 border-t space-x-3 ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
              <button
                type="button"
                onClick={() => setToleranceModalOpen(false)}
                className={`px-4 py-2 rounded-lg transition-colors ${
                  isDarkMode
                    ? "text-rp-grey/70 hover:text-rp-grey hover:bg-rp-surface-raised"
                    : "text-gray-600 hover:text-gray-700 hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleFindMatches}
                className="px-4 py-2 text-white rounded-lg transition-colors bg-blue-600 hover:bg-blue-700"
              >
                Find Matches
              </button>
            </div>
          </div>
        </div>
      )}
      
      <ConfirmationModal
        isOpen={showChangesModal}
        onClose={() => setShowChangesModal(false)}
        onConfirm={() => saveChanges(contracts)}
        title="Save Changes"
        message={
            (
            <div className="max-h-96 overflow-y-auto">
              <p className="mb-4">You are about to save changes to {pendingChanges.size} contracts:</p>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2">ConID</th>
                    <th className="py-2">Field</th>
                    <th className="py-2">Old Value</th>
                    <th className="py-2">New Value</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(pendingChanges.entries()).map(([conId, fields]) => (
                    Object.entries(fields).map(([key, { oldValue, newValue }]) => (
                      <tr key={`${conId}-${key}`} className="border-b border-gray-100 dark:border-gray-800">
                        <td className="py-2 font-mono">{conId}</td>
                        <td className="py-2">{key}</td>
                        <td className="py-2 text-red-500 line-through">{oldValue || "(empty)"}</td>
                        <td className="py-2 text-green-500">{newValue}</td>
                      </tr>
                    ))
                  ))}
                </tbody>
              </table>
            </div>
            ) as any // Cast to any because ConfirmationModal message is typed as string but supports nodes in some versions or I should check type
        }
        confirmText={savingChanges ? "Saving..." : "Save Changes"}
        loading={savingChanges}
      />

      <ConfirmationModal
        isOpen={verifyMatchesModalOpen}
        onClose={() => {
          setVerifyMatchesModalOpen(false);
          setMatchingConIdsForVerify([]);
        }}
        onConfirm={handleConfirmVerifyMatches}
        title="Verify Matching Contracts"
        message={`Found ${matchingConIdsForVerify.length} contracts with prices matching within ${tolerancePercent}% tolerance across IBKR, Yahoo, and EODHD. Verify them now?`}
        confirmText="Verify"
      />

      {tradableTimesContract && (
        <TradableTimesModal
          isOpen={tradableTimesModalOpen}
          onClose={() => {
            setTradableTimesModalOpen(false);
            setTradableTimesContract(null);
          }}
          tradableTimesUtc={tradableTimesContract.tradableTimesUtc || null}
          symbol={tradableTimesContract.symbol}
          timeZoneId={tradableTimesContract.timeZoneId || null}
        />
      )}

      {livePriceContract && (
        <LivePriceModal
          isOpen={livePriceModalOpen}
          onClose={() => {
            setLivePriceModalOpen(false);
            setLivePriceContract(null);
          }}
          contract={livePriceContract}
          onContractUpdated={contractActions.fetchContracts}
        />
      )}

      {usageContract && (
        <ContractUsageModal
          isOpen={true}
          onClose={() => setUsageContract(null)}
          conId={usageContract.conId}
          symbol={usageContract.symbol}
        />
      )}

      {referenceDataContract && (
        <ContractReferenceDataModal
          isOpen={true}
          onClose={() => setReferenceDataContract(null)}
          contract={referenceDataContract}
          onSaved={contractActions.fetchContracts}
        />
      )}
    </AdminPageLayout>
  );
};

export default ContractsPage;
